import React, { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, Camera, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { INPUT_FIELD_STYLES, FORM_CONTAINER_STYLES, FORM_CONTENT_STYLES, TAB_CONTAINER_STYLES } from './shared-styles';

interface Photo {
  id: string;
  content: {
    photo_url: string;
    caption?: string;
  };
  submitted_at: string;
}

interface PhotoGalleryProps {
  photos: Photo[];
  onSubmitPhoto: (photoUrl: string, caption?: string) => Promise<void>;
  onSubmitFile: (file: File, caption?: string) => Promise<void>;
  loading: boolean;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photos,
  onSubmitPhoto,
  onSubmitFile,
  loading
}) => {
  const [photoUrl, setPhotoUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [fileCaption, setFileCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!photoUrl.trim()) return;
    await onSubmitPhoto(photoUrl.trim(), caption.trim() || undefined);
    setPhotoUrl('');
    setCaption('');
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    await onSubmitFile(selectedFile, fileCaption.trim() || undefined);
    setSelectedFile(null);
    setFileCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Photo submission section */}
      <div className="bg-warm-cream/30 rounded-lg p-5 border border-sunset-medium/10">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-peach-orange/10">
            <Camera className="h-5 w-5 text-peach-orange" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-sunset-dark">Share Photos</h3>
            <p className="text-sm text-sunset-medium">Upload or link photos of this terrace</p>
          </div>
        </div>

        <Tabs defaultValue="url" className={TAB_CONTAINER_STYLES}>
          <TabsList className="mb-4">
            <TabsTrigger value="url" className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Share URL
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Upload File
            </TabsTrigger>
          </TabsList>

          <TabsContent value="url" className={FORM_CONTAINER_STYLES}>
            <form onSubmit={handleUrlSubmit} className={FORM_CONTENT_STYLES}>
              <Label htmlFor="photoUrl" className="text-base font-semibold text-sunset-dark">
                Photo URL
              </Label>
              <Input
                id="photoUrl"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                className={INPUT_FIELD_STYLES}
              />
              <Label htmlFor="urlCaption" className="text-base font-semibold text-sunset-dark">
                Caption <span className="text-sunset-medium font-normal">(optional)</span>
              </Label>
              <Input
                id="urlCaption"
                type="text"
                placeholder="Describe your photo..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className={INPUT_FIELD_STYLES}
              />
              <Button 
                type="submit" 
                disabled={!photoUrl.trim() || loading}
                variant="action"
                size="lg"
              >
                <Upload className="h-5 w-5 mr-2" />
                Share Photo
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="upload" className={FORM_CONTAINER_STYLES}>
            <form onSubmit={handleFileSubmit} className={FORM_CONTENT_STYLES}>
              <Label htmlFor="photoFile" className="text-base font-semibold text-sunset-dark">
                Choose Photo
              </Label>
              <Input
                id="photoFile"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className={`${INPUT_FIELD_STYLES} file:h-10 file:rounded-md file:border-0 file:bg-peach-orange/10 file:text-peach-orange file:font-medium file:mr-4 hover:file:bg-peach-orange/20 transition-colors`}
              />
              {selectedFile && (
                <div className="mt-2 p-3 bg-peach-orange/5 rounded-lg border border-peach-orange/20">
                  <p className="text-sm text-sunset-dark font-medium">
                    Selected: {selectedFile.name}
                  </p>
                  <p className="text-xs text-sunset-medium">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
              <Label htmlFor="fileCaption" className="text-base font-semibold text-sunset-dark">
                Caption <span className="text-sunset-medium font-normal">(optional)</span>
              </Label>
              <Input
                id="fileCaption"
                type="text"
                placeholder="Describe your photo..."
                value={fileCaption}
                onChange={(e) => setFileCaption(e.target.value)}
                className={INPUT_FIELD_STYLES}
              />
              <Button 
                type="submit" 
                disabled={!selectedFile || loading}
                variant="action"
                size="lg"
              >
                <Camera className="h-5 w-5 mr-2" />
                Upload Photo
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo gallery */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-sunset-dark">Recent Photos</h4>
          <Badge variant="secondary" className="bg-peach-orange/10 text-peach-orange border-peach-orange/20">
            {photos.length} photos
          </Badge>
        </div>
        
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {photos.slice(0, 6).map((photo, index) => (
              <Card 
                key={photo.id} 
                className={`relative overflow-hidden ${
                  index === 0 ? 'col-span-2 aspect-video' : 'aspect-square'
                } hover:shadow-lg transition-all duration-300 border-sunset-medium/20`}
              >
                <img 
                  src={photo.content.photo_url} 
                  alt={photo.content.caption || 'Visitor photo'} 
                  className="w-auto max-w-md mx-auto h-full object-cover" 
                />
                {photo.content.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent text-white p-3">
                    <p className="text-sm font-medium">{photo.content.caption}</p>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="bg-black/20 text-white border-white/20 text-xs">
                    {new Date(photo.submitted_at).toLocaleDateString()}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-warm-cream/20 rounded-lg border-2 border-dashed border-sunset-medium/30">
            <ImageIcon className="h-16 w-16 mx-auto mb-4 text-sunset-medium/50" />
            <h5 className="text-lg font-medium text-sunset-dark mb-2">No photos yet</h5>
            <p className="text-sunset-medium">Be the first to share a photo of this terrace!</p>
          </div>
        )}
      </div>
    </div>
  );
};
